import {
  type MailIdentityKind,
  OWN_SERVER_DEFAULTS,
  type PublicMailIdentity,
} from '@embertime/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Link } from '@tanstack/react-router'
import { Bell, ChevronRight, Cloud, Plus, Server, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { AddAccountDialog } from '../components/mail/add-account-dialog'
import { IdentityAddSheet } from '../components/mail/identity-add-sheet'
import { IdentityList } from '../components/mail/identity-list'
import { IdentityOwnDomainSheet } from '../components/mail/identity-own-domain-sheet'
import { IdentitySignatureSheet } from '../components/mail/identity-signature-sheet'
import { IdentitySmtpSheet } from '../components/mail/identity-smtp-sheet'
import { ProtectionBadge, ProtectionSubtitle } from '../components/mail/protection-badge'
import { ProtectionDialog } from '../components/mail/protection-dialog'
import { SettingsLayout } from '../components/settings-layout'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { api } from '../lib/api'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'
import { MailCompaniesSettings } from './settings-mail-companies'
import { MailTemplatesSettings } from './settings-mail-templates'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/mail-accounts',
  component: MailAccountSettings,
})

/** Open sender identity sheet (#839): type choice, or the form of one kind
 * (with `identity` = edit a saved one). */
type IdentitySheet =
  | { step: 'kind'; accountId: string; accountLabel: string; kind?: MailIdentityKind }
  | {
      step: 'form'
      accountId: string
      accountLabel: string
      kind: MailIdentityKind
      identity?: PublicMailIdentity
    }

function MailAccountSettings() {
  const qc = useQueryClient()
  const [protectionId, setProtectionId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [identitySheet, setIdentitySheet] = useState<IdentitySheet | null>(null)
  const [signatureSheet, setSignatureSheet] = useState<{
    identity: PublicMailIdentity
    accountId: string
  } | null>(null)
  const { data, isPending, error } = useQuery({
    queryKey: ['mail-accounts'],
    queryFn: () => api.listMailAccounts(),
  })
  const update = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'enable' | 'disable' | 'delete' }) => {
      if (action === 'delete') await api.deleteMailAccount(id)
      else if (action === 'disable') await api.disableMailAccount(id)
      else await api.enableMailAccount(id)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail-accounts'] }),
    onError: (err: Error) => notify.error(err.message),
  })
  return (
    <SettingsLayout title="Mail-Konten">
      <div className="max-w-2xl space-y-4">
        <div>
          <h1 className="text-xl font-semibold">Mail-Konten</h1>
          <p className="text-sm text-text-secondary mt-1">
            Postfächer für Lesen, Antworten und Rechnungsversand
          </p>
        </div>
        {/* #774: Unterseite für die Push-Meldungen je Konto (Mockup Mail 1c). */}
        <Link
          to="/settings/mail-push"
          className="flex min-h-11 items-center gap-3 rounded-2xl border border-border-default bg-bg-elevated p-4 hover:bg-bg-overlay"
        >
          <Bell className="h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="block font-medium">Push-Meldungen</span>
            <span className="block text-xs text-text-secondary">
              Neue Mails als Meldung auf dem Handy – je Konto
            </span>
          </span>
          <ChevronRight className="h-5 w-5 shrink-0 text-text-tertiary" aria-hidden="true" />
        </Link>
        {isPending && <p className="text-sm text-text-tertiary">Konten werden geladen …</p>}
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error.message}
          </p>
        )}
        {data?.accounts.map((account) => (
          <article
            key={account.id}
            className={`rounded-2xl border p-4 space-y-3 ${account.disabledAt ? 'border-border-subtle bg-bg-elevated/40 opacity-65' : 'border-accent/20 bg-bg-elevated'}`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`rounded-xl p-2.5 ${account.disabledAt ? 'bg-bg-overlay text-text-tertiary' : 'bg-accent/10 text-accent'}`}
              >
                {account.authType === 'oauth-microsoft' ? (
                  <Cloud className="h-5 w-5" />
                ) : (
                  <Server className="h-5 w-5" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="flex flex-wrap items-center gap-2 font-medium">
                  {account.label}
                  {account.protected && <ProtectionBadge />}
                </h2>
                <p className="text-xs text-text-secondary break-all">{account.email}</p>
                {account.protected && <ProtectionSubtitle />}
                <p className="text-xs text-text-tertiary mt-1">
                  {account.authType === 'oauth-microsoft'
                    ? 'Microsoft OAuth · Azure-AD-App'
                    : 'IMAP + SMTP · App-Passwort'}
                </p>
              </div>
            </div>
            <p
              className={`text-xs ${account.disabledAt ? 'text-text-secondary' : account.lastSyncError ? 'text-danger' : 'text-success'}`}
            >
              {account.disabledAt
                ? 'Wird abgelöst. Bleibt bis zum Umzug lesbar, neue Mails laufen über den eigenen Server. Kein doppelter Posteingang.'
                : account.lastSyncError
                  ? `Fehler: ${account.lastSyncError}`
                  : account.lastSyncAt
                    ? 'verbunden'
                    : 'Noch nicht synchronisiert'}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="ghost" onClick={() => setProtectionId(account.id)}>
                Schutz bearbeiten
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={update.isPending}
                onClick={() =>
                  update.mutate({
                    id: account.id,
                    action: account.disabledAt ? 'enable' : 'disable',
                  })
                }
              >
                {account.disabledAt ? 'Wieder aktivieren' : 'Deaktivieren'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={update.isPending}
                onClick={async () => {
                  if (
                    await confirm({
                      title: 'Konto trennen?',
                      description:
                        'Das Konto und lokal gespeicherte Mails werden aus Ember entfernt. Die Mails auf dem Server bleiben erhalten.',
                      confirmLabel: 'Konto trennen',
                      variant: 'danger',
                    })
                  )
                    update.mutate({ id: account.id, action: 'delete' })
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {account.disabledAt ? 'Nach Umzug trennen' : 'Trennen'}
              </Button>
            </div>
            <IdentityList
              account={account}
              onAdd={() =>
                setIdentitySheet({
                  step: 'kind',
                  accountId: account.id,
                  accountLabel: account.label,
                })
              }
              onEdit={(identity) =>
                setIdentitySheet({
                  step: 'form',
                  accountId: account.id,
                  accountLabel: account.label,
                  kind: identity.kind,
                  identity,
                })
              }
            />
            <SignatureLinks
              accountId={account.id}
              onOpen={(identity) => setSignatureSheet({ identity, accountId: account.id })}
            />
          </article>
        ))}
        <MailCompaniesSettings />
        <MailTemplatesSettings />
        <section className="rounded-2xl border border-accent/25 bg-accent/5 p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Server className="h-6 w-6 text-accent shrink-0" />
            <div>
              <h2 className="font-semibold">
                Eigener Server{' '}
                <span className="ml-1 text-xs font-normal text-accent">Empfohlen</span>
              </h2>
              <p className="text-xs text-text-secondary">
                {OWN_SERVER_DEFAULTS.imapHost} · IMAP + SMTP
              </p>
            </div>
          </div>
          <Button className="w-full" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            Konto hinzufügen
          </Button>
        </section>
        <p className="text-xs text-text-tertiary">
          Deaktivierte Konten bleiben lokal lesbar. Trenne das alte Konto erst nach dem
          vollständigen Umzug Deiner Mails.
        </p>
      </div>
      {adding && (
        <AddAccountDialog
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false)
            qc.invalidateQueries({ queryKey: ['mail-accounts'] })
          }}
        />
      )}
      {identitySheet?.step === 'kind' && (
        <IdentityAddSheet
          open
          accountLabel={identitySheet.accountLabel}
          initialKind={identitySheet.kind}
          onClose={() => setIdentitySheet(null)}
          onNext={(kind) => setIdentitySheet({ ...identitySheet, step: 'form', kind })}
        />
      )}
      {identitySheet?.step === 'form' &&
        (() => {
          const props = {
            open: true,
            accountId: identitySheet.accountId,
            accountLabel: identitySheet.accountLabel,
            identity: identitySheet.identity,
            onBack: () =>
              setIdentitySheet({
                step: 'kind',
                accountId: identitySheet.accountId,
                accountLabel: identitySheet.accountLabel,
                kind: identitySheet.kind,
              }),
            onClose: () => setIdentitySheet(null),
            onSaved: () => setIdentitySheet(null),
          }
          return identitySheet.kind === 'own_domain' ? (
            <IdentityOwnDomainSheet key={identitySheet.identity?.id ?? 'new'} {...props} />
          ) : (
            <IdentitySmtpSheet key={identitySheet.identity?.id ?? 'new'} {...props} />
          )
        })()}
      {protectionId && (
        <ProtectionDialog accountId={protectionId} onClose={() => setProtectionId(null)} />
      )}
      {signatureSheet && (
        <IdentitySignatureSheet
          key={signatureSheet.identity.id}
          identity={signatureSheet.identity}
          accountId={signatureSheet.accountId}
          onClose={() => setSignatureSheet(null)}
        />
      )}
    </SettingsLayout>
  )
}

function SignatureLinks({
  accountId,
  onOpen,
}: {
  accountId: string
  onOpen: (identity: PublicMailIdentity) => void
}) {
  const { data } = useQuery({
    queryKey: ['mail-identities', accountId],
    queryFn: () => api.listMailIdentities(accountId),
  })
  if (!data?.identities.length) return null
  return (
    <div className="-mx-4 border-t border-border-subtle px-4 pt-3">
      <p className="mb-2 text-xs text-text-tertiary">Signatur je Absender</p>
      <div className="flex flex-wrap gap-2">
        {data.identities.map((identity) => (
          <button
            key={identity.id}
            type="button"
            onClick={() => onOpen(identity)}
            className="rounded-full border border-border-default px-3 py-2 text-xs text-accent hover:bg-accent/10"
          >
            Signatur · {identity.displayName ?? identity.address}
          </button>
        ))}
      </div>
    </div>
  )
}
